/**
 * /tenants/$slug/directory/ad-inbound/$directoryId — detail route
 * shim.
 *
 * Owns every bit of this tabbed page's view state in the URL,
 * following the pattern `agents/$agentId.tsx` established:
 *   - tab                selects Settings / Attribute mapping / Users / Groups.
 *   - users_offset/users_sync_status/users_q      Users tab's pagination+filter.
 *   - groups_offset/groups_sync_status/groups_q   Groups tab's pagination+filter.
 * The `users_`/`groups_` prefixes keep the two tabs' otherwise
 * identically-named filters from colliding in one query string.
 */

import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { adInboundGroupsSearchSchema } from "@/features/directory/ad-inbound-groups-search";
import { adInboundUsersSearchSchema } from "@/features/directory/ad-inbound-users-search";
import { fetchAdInboundDirectory } from "@/features/directory/queries";
import { queryKeys } from "@/lib/query-keys";
import { AdInboundDetailPage } from "@/features/directory/pages/ad-inbound-detail-page";

const tabSearchSchema = z.object({
  tab: z.enum(["settings", "attribute-mapping", "users", "groups", "rejects"]).optional(),
  rejects_offset: z.coerce.number().int().nonnegative().optional(),
});

const searchSchema = tabSearchSchema
  .merge(adInboundUsersSearchSchema)
  .merge(adInboundGroupsSearchSchema);

export const Route = createFileRoute("/_authed/tenants/$slug/directory/ad-inbound/$directoryId")({
  validateSearch: searchSchema,
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData({
      queryKey: queryKeys.directory.adInbound.detail(context.tenant.id, params.directoryId),
      queryFn: () => fetchAdInboundDirectory(context.tenant.id, params.directoryId),
    }),
  component: AdInboundDetailPage,
});
